import { renderToStaticMarkup } from "react-dom/server";
import { Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Mascot } from "@/components/game/Mascot";

const W = 1920;
const H = 1080;
const MEDAL = {
  1: { c1: "#FDE047", c2: "#CA8A04", edge: "#A16207", r: 1 },
  2: { c1: "#F1F5F9", c2: "#94A3B8", edge: "#64748B", r: 0.72 },
  3: { c1: "#FDBA74", c2: "#C2410C", edge: "#9A3412", r: 0.58 },
};
const SIGNATURE = "Furkan Buyrukbilen · hi@furkan.agency";

const loadImg = (src) => new Promise((res) => { const i = new Image(); i.onload = () => res(i); i.onerror = () => res(null); i.src = src; });
const mascotImg = (id) => loadImg(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(renderToStaticMarkup(<Mascot id={id} size={220} />))}`);

const glow = (ctx, x, y, r, color) => {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, color);
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
};

const rrect = (ctx, x, y, w, h, r, top = false) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  if (top) { ctx.lineTo(x + w, y + h); ctx.lineTo(x, y + h); } else {
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  }
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

const crown = (ctx, cx, y, s) => {
  ctx.fillStyle = "#FACC15";
  ctx.beginPath();
  [[-0.9, 0.8], [-1, 0], [-0.42, 0.4], [0, -0.16], [0.42, 0.4], [1, 0], [0.9, 0.8]].forEach(([dx, dy], i) => (i ? ctx.lineTo : ctx.moveTo).call(ctx, cx + dx * s, y + dy * s));
  ctx.closePath();
  ctx.fill();
};

const fit = (ctx, text, max, size, family = "Fredoka", weight = 700) => {
  let s = size;
  ctx.font = `${weight} ${s}px ${family}, sans-serif`;
  while (ctx.measureText(text).width > max && s > 14) { s -= 2; ctx.font = `${weight} ${s}px ${family}, sans-serif`; }
};

const drawStep = (ctx, row, place, cx, sw, base, maxH, img) => {
  const m = MEDAL[place];
  const h = maxH * m.r;
  const top = base - h;
  const g = ctx.createLinearGradient(0, top, 0, base);
  g.addColorStop(0, m.c1);
  g.addColorStop(1, m.c2);
  ctx.fillStyle = g;
  ctx.shadowColor = m.edge;
  ctx.shadowBlur = 24;
  rrect(ctx, cx - sw / 2, top, sw, h, Math.min(22, sw / 6), true);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.fillStyle = m.edge;
  ctx.fillRect(cx - sw / 2, base - 6, sw, 6);
  ctx.fillStyle = "rgba(11,15,22,0.8)";
  ctx.textAlign = "center";
  ctx.font = `700 ${Math.max(22, Math.min(64, h * 0.42))}px Fredoka, sans-serif`;
  ctx.fillText(`${place}.`, cx, top + Math.max(26, Math.min(70, h * 0.48)));
  ctx.fillStyle = "#E2E8F0";
  fit(ctx, row.value, sw + 20, 24, "Outfit", 600);
  ctx.fillText(row.value, cx, top - 10);
  ctx.fillStyle = row.color;
  fit(ctx, row.name, sw + 30, 32);
  ctx.fillText(row.name, cx, top - 42);
  const a = Math.min(84, sw * 0.5);
  const ay = top - 78 - a;
  if (img) ctx.drawImage(img, cx - a / 2, ay, a, a);
  if (place === 1) crown(ctx, cx, ay - (img ? 30 : -a + 20), 24);
};

const drawCard = (ctx, card, x, y, w, h, imgs) => {
  ctx.fillStyle = "rgba(255,255,255,0.045)";
  rrect(ctx, x, y, w, h, 28);
  ctx.fill();
  ctx.strokeStyle = "rgba(148,163,184,0.25)";
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.textAlign = "center";
  ctx.fillStyle = "#FACC15";
  fit(ctx, card.title, w - 40, 34);
  ctx.fillText(card.title, x + w / 2, y + 48);
  const base = y + h;
  const sw = Math.min(220, (w - 60) / 3);
  const gap = sw + 14;
  const maxH = Math.max(40, Math.min(300, h - 80 - 200));
  [[2, -gap], [1, 0], [3, gap]].forEach(([place, dx]) => {
    const row = card.rows[place - 1];
    if (row) drawStep(ctx, row, place, x + w / 2 + dx, sw, base, maxH, row.mascot ? imgs[row.mascot] : null);
  });
};

export const drawBadge = async (badge) => {
  await Promise.all(["700 64px Fredoka", "600 32px Outfit"].map((f) => document.fonts.load(f).catch(() => null)));
  const sections = badge.sections || [];
  const ids = [...new Set(sections.flatMap((s) => s.cards.flatMap((c) => c.rows.map((r) => r.mascot).filter(Boolean))))];
  const loaded = await Promise.all(ids.map(mascotImg));
  const imgs = Object.fromEntries(ids.map((id, i) => [id, loaded[i]]));

  const cv = document.createElement("canvas");
  cv.width = W;
  cv.height = H;
  const ctx = cv.getContext("2d");
  ctx.fillStyle = "#0d1117";
  ctx.fillRect(0, 0, W, H);
  glow(ctx, W, 0, 900, "rgba(250,204,21,0.20)");
  glow(ctx, 0, H, 900, "rgba(34,197,94,0.22)");
  ctx.strokeStyle = "rgba(250,204,21,0.35)";
  ctx.lineWidth = 4;
  ctx.strokeRect(36, 36, W - 72, H - 72);

  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "left";
  ctx.font = "700 56px Fredoka, sans-serif";
  ctx.fillStyle = "#22C55E";
  ctx.fillText("FChem", 80, 120);
  ctx.textAlign = "right";
  ctx.font = "600 32px Outfit, sans-serif";
  ctx.fillStyle = "#CBD5E1";
  ctx.fillText(new Date().toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" }), W - 80, 116);
  ctx.textAlign = "center";
  ctx.fillStyle = "#FFFFFF";
  fit(ctx, `Oyun bitti! ${badge.title}`, 1300, 64);
  ctx.fillText(`Oyun bitti! ${badge.title}`, W / 2, 120);
  ctx.font = "600 24px Outfit, sans-serif";
  ctx.fillStyle = "#9CA3AF";
  ctx.fillText("B A Ş A R I   R O Z E T İ  ·  K A Z A N A N L A R", W / 2, 165);

  const top = 195;
  const bottom = H - 100;
  const secH = (bottom - top) / Math.max(1, sections.length);
  sections.forEach((s, si) => {
    const y = top + si * secH;
    ctx.textAlign = "left";
    ctx.fillStyle = "#94A3B8";
    ctx.font = "700 24px Outfit, sans-serif";
    ctx.fillText(s.label.toLocaleUpperCase("tr"), 80, y + 26);
    const n = s.cards.length;
    const total = W - 160;
    const gap = 24;
    const weights = s.cards.map((_, i) => (i === 0 && n > 1 ? 1.3 : 1));
    const sum = weights.reduce((a, b) => a + b, 0);
    let x = n === 1 ? (W - 900) / 2 : 80;
    s.cards.forEach((c, i) => {
      const w = n === 1 ? 900 : ((total - gap * (n - 1)) * weights[i]) / sum;
      drawCard(ctx, c, x, y + 40, w, secH - 56, imgs);
      x += w + gap;
    });
  });

  ctx.textAlign = "center";
  ctx.font = "600 26px Outfit, sans-serif";
  ctx.fillStyle = "#CBD5E1";
  ctx.fillText(SIGNATURE, W / 2, H - 56);
  return cv;
};

export const downloadBadge = async (badge) => {
  const cv = await drawBadge(badge);
  const a = document.createElement("a");
  a.href = cv.toDataURL("image/png");
  a.download = `fchem-rozet-${new Date().toISOString().slice(0, 10)}.png`;
  a.click();
};

export const BadgeButton = ({ badge, className = "" }) => (
  <Button variant="outline" onClick={() => downloadBadge(badge)} className={className} data-testid="download-badge-button">
    <Award className="h-4 w-4 mr-2" />Rozet kartını indir
  </Button>
);
